<script lang="ts">
  import Drawer from '@athena-lib/Header/Drawer/Drawer.svelte';
  import MiniCart, { type ClickAction } from '@athena-lib/MiniCart/MiniCart.svelte';
  import { Close, Spinner } from '@athena-lib/Icons';
  import { focusTrap } from '@athena-lib/focusTrap.svelte';
  import {
    createCurrencyFormatter,
    type CheckoutSdk,
    getIsManualOrder,
  } from '@athena-lib/Checkout/utils';
  import { lineItemToQuoteCard } from '@athena-lib/QuoteCard/QuoteCard.svelte';
  import { CheckoutLoginForm } from '@athena-lib/CheckoutLoginForm';
  import type { LoginFormHandler } from '@athena-lib/LoginForm';
  import { getLocalizationContext } from '@athena-lib/i18n';
  import { getCustomStickersBundleProps } from '@athena-lib/CustomStickersBundle';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import type { LoginFormTrackingEvent } from '@athena-lib/LoginForm/types';
  import clsx from 'clsx';
  import { openEditor } from './GlobalEditor.svelte';
  import {
    checkoutDrawerContext,
    checkoutDrawerActions,
    lastUrl,
  } from '$lib/utilities/checkoutDrawerContext.svelte';
  import { getProductsTranslators } from '$lib/checkout.svelte';
  import {
    getCartEventsTracker,
    getEnhancedCommereceEventsTracker,
  } from '$lib/gtmEventHelper.svelte';
  import { onNavigate } from '$app/navigation';

  type CheckoutDrawerProps = {
    checkoutSdk: CheckoutSdk;
    onSubmitLogin: LoginFormHandler;
    onSubmitResetPassword: LoginFormHandler;
    onSubmitSignUp: LoginFormHandler;
    onTracking?: (message: LoginFormTrackingEvent) => void;
  };

  const {
    checkoutSdk,
    onSubmitLogin,
    onSubmitResetPassword,
    onSubmitSignUp,
    onTracking,
  }: CheckoutDrawerProps = $props();

  const formatCurrency = $derived(createCurrencyFormatter(checkoutSdk.state));
  const close = checkoutDrawerActions.close;
  const isOpen = $derived(checkoutDrawerContext.value !== null);

  const cartEventsTracker = getCartEventsTracker('cart_drawer');
  const commerceEventsTracker = getEnhancedCommereceEventsTracker(() => checkoutSdk);

  const translations = getCommonTranslations();

  let oldPage = $state(checkoutDrawerContext?.value?.type);
  $effect(() => {
    const newPage = checkoutDrawerContext?.value?.type;
    if (newPage === 'MiniCart' && oldPage !== newPage) {
      commerceEventsTracker.viewCart({ eventAction: 'view_cart_modal' });
      cartEventsTracker.viewMiniCart();
    }
    oldPage = newPage;
  });

  $effect(() => {
    onNavigate((url) => {
      lastUrl.set(url.from?.url.href);
    });
    if (
      checkoutDrawerContext?.value?.type !== 'MiniCart' &&
      checkoutDrawerContext?.value?.type !== 'DeliveryDetails' &&
      checkoutDrawerContext?.value?.type !== 'ItemAdded'
    ) {
      return;
    }
  });

  function actionClicked(action: ClickAction) {
    if (action === 'review_cart') {
      cartEventsTracker.viewReviewCart();
    }

    if (action == 'checkout_now') {
      cartEventsTracker.goToCheckout();
    }
    close();
  }
</script>

<Drawer
  label="checkout-drawer"
  variant="minicart"
  onClose={close}
  {isOpen}
  disablePadding
  widthClass={checkoutDrawerContext?.value?.type === 'ItemAdded'
    ? 'lg:max-w-[471px]'
    : 'md:max-w-full lg:max-w-[30rem]'}
  heightClass={checkoutDrawerContext?.value?.type === 'ItemAdded' ? 'h-[430px]' : ''}
  borderClass={checkoutDrawerContext?.value?.type === 'ItemAdded'
    ? 'rounded-t-lg lg:rounded-lg'
    : ''}
  offsetClass={checkoutDrawerContext?.value?.type === 'ItemAdded' ? 'lg:top-2 lg:right-2' : ''}
  overlayClass={'bottom-0 !fixed w-full'}
  extraClasses={'!relative lg:!fixed'}
>
  <div tabindex="-1" class="h-full" use:focusTrap role="dialog">
    <div class="absolute flex w-full flex-col items-end" aria-label={translations.addedToCart}>
      <button
        type="button"
        class={clsx(
          'ml-auto rounded font-light',
          checkoutDrawerContext?.value?.type === 'ItemAdded'
            ? 'mr-7 mt-7 flex items-center justify-center'
            : 'px-6 py-4',
        )}
        onclick={close}
        aria-label={translations.closeText}
      >
        <Close
          sizeClass={checkoutDrawerContext?.value?.type === 'ItemAdded' ? 'h-8 w-8' : 'h-6 w-6'}
        ></Close>
      </button>
    </div>

    {#if checkoutDrawerContext?.value?.type === 'Login'}
      <CheckoutLoginForm
        onLogin={onSubmitLogin}
        onResetPassword={onSubmitResetPassword}
        onCreateAccount={onSubmitSignUp}
        googleLoginHref="/customer/google_login"
        {onTracking}
      />
    {:else if !checkoutSdk.state.isLoading && checkoutDrawerContext?.value?.type === 'ItemAdded' && checkoutSdk.state.lines.length > 0}
      {@const bundleSavings = getCustomStickersBundleProps(checkoutSdk.state, formatCurrency)}
      {@const latestAddedItem = checkoutSdk.state.lines[0]}
      <MiniCart
        isManualOrder={getIsManualOrder(checkoutSdk.state)}
        quoteCard={lineItemToQuoteCard(
          latestAddedItem,
          checkoutSdk.state.tax?.type,
          formatCurrency,
          checkoutSdk.actions,
          cartEventsTracker,
          () => openEditor(checkoutSdk, latestAddedItem, 'mini_cart_editor'),
          checkoutSdk.getCustomSticker,
          getProductsTranslators(),
          getLocalizationContext(),
          getCommonTranslations(),
        )}
        checkoutUrl={checkoutSdk.state.checkoutPageUrl}
        reviewCartUrl={`${checkoutSdk.state.checkoutPageUrl}?preview`}
        onActionClicked={actionClicked}
        expressCheckoutElementId={checkoutSdk.state.checkoutElements.express?.payNowElementId}
        customStickersBundle={bundleSavings}
        totalItems={checkoutSdk.state.lines.length}
      ></MiniCart>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <Spinner sizeClass="h-12 w-12" />
      </div>
    {/if}
  </div>
</Drawer>

<style>
</style>
